import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '../hooks/useUser'
import TrainingSetService from '../services/trainingSet'
import ApiService from '../services/ApiService'
import PaginationControls from './PaginationControls'

function TrainingSets() {
  const navigate = useNavigate()
  const { currentUser } = useUser()
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [trainingSets, setTrainingSets] = useState([])
  const [pagination, setPagination] = useState(null)
  const [labels, setLabels] = useState([])
  const [currentPage, setCurrentPage] = useState(() => {
    const pageFromUrl = searchParams.get('page')
    return pageFromUrl ? Number(pageFromUrl) : 1
  })
  const [perPage, setPerPage] = useState(() => {
    const userId = currentUser?.id || 'guest'
    const saved = localStorage.getItem(`trainingSets_perPage_${userId}`)
    return saved ? Number(saved) : 20
  })
  const [columnLayout, setColumnLayout] = useState(() => {
    const userId = currentUser?.id || 'guest'
    const saved = localStorage.getItem(`trainingSets_columnLayout_${userId}`)
    return saved ? Number(saved) : 1
  })
  const [selectedLabel, setSelectedLabel] = useState('')
  const [selectedSet, setSelectedSet] = useState('Ai')

  const loadTrainingSets = useCallback(async (page = 1, itemsPerPage = perPage, label = selectedLabel, set = selectedSet) => {
    try {
      setLoading(true)
      setError(null)
      const response = await TrainingSetService.getTrainingSets(page, itemsPerPage, label, set)

      if (response.success) {
        setTrainingSets(response.data.data.training_sets)
        setPagination(response.data.data.pagination)
      } else {
        setError(response.error || 'Failed to load training sets')
      }
    } catch {
      setError('Network error occurred while loading training sets')
    } finally {
      setLoading(false)
    }
  }, [perPage, selectedLabel, selectedSet])

  useEffect(() => {
    ApiService.getVersion()
      .then(version => {
        setLabels(version.labels || [])
      })
      .catch(() => {
        console.warn('Failed to load labels')
      })
  }, [])

  useEffect(() => {
    loadTrainingSets(currentPage, perPage, selectedLabel, selectedSet)
  }, [currentPage, perPage, selectedLabel, selectedSet, loadTrainingSets])

  useEffect(() => {
    setSearchParams({ page: currentPage })
  }, [currentPage, setSearchParams])

  useEffect(() => {
    const userId = currentUser?.id || 'guest'
    localStorage.setItem(`trainingSets_perPage_${userId}`, perPage)
  }, [perPage, currentUser])

  useEffect(() => {
    const userId = currentUser?.id || 'guest'
    localStorage.setItem(`trainingSets_columnLayout_${userId}`, columnLayout)
  }, [columnLayout, currentUser])

  const formatLabelForDisplay = (label) => {
    if (!label) return ''
    return label
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const parseEvidence = (evidenceString) => {
    if (!evidenceString) return []
    try {
      const parsed = JSON.parse(evidenceString)
      if (Array.isArray(parsed)) {
        return parsed.map(item => typeof item === 'string' ? item : JSON.stringify(item))
      }
      return [typeof parsed === 'string' ? parsed : JSON.stringify(parsed)]
    } catch {
      return [String(evidenceString)]
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Never'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const maskEmail = (email) => {
    if (!email || typeof email !== 'string') return 'N/A'
    const [localPart, domain] = email.split('@')
    if (!domain) return email
    if (localPart.length <= 2) {
      return `${localPart[0]}***@${domain}`
    }
    return `${localPart[0]}${'*'.repeat(localPart.length - 2)}${localPart[localPart.length - 1]}@${domain}`
  }

  const handlePreviousPage = () => {
    if (pagination?.has_prev) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleNextPage = () => {
    if (pagination?.has_next) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value))
    setCurrentPage(1)
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  const handleLabelFilterChange = (e) => {
    setSelectedLabel(e.target.value)
    setCurrentPage(1)
  }

  const handleSetFilterChange = (e) => {
    setSelectedSet(e.target.value)
    setCurrentPage(1)
  }

  const toggleColumnLayout = () => {
    setColumnLayout(columnLayout === 3 ? 1 : columnLayout + 1)
  }

  const getLayoutIcon = () => {
    if (columnLayout === 1) return '▢'
    if (columnLayout === 2) return '▢▢'
    return '▢▢▢'
  }

  if (loading) {
    return (
      <div className="manage-users-wrapper">
        <div className="manage-users">
          <div className="loading-state">
            <div>Loading...</div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="manage-users-wrapper">
      <div className="manage-users">
        <div className="breadcrumb">
          <button
            className="breadcrumb-link"
            onClick={() => navigate('/')}
          >
            Chat Home
          </button>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item">Training Sets</span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-current">Approve Labels</span>
        </div>

        <div className="page-header">
          <div>
            <h1>Approve Labels</h1>
            <p>Review and approve predicted labels for training data</p>
          </div>
          <div className="header-controls">
            <div className="set-filter">
              <label htmlFor="set-filter">Set:</label>
              <select
                id="set-filter"
                value={selectedSet}
                onChange={handleSetFilterChange}
                className="set-filter-dropdown"
              >
                <option value="Ai">Ai</option>
                <option value="Gary">Gary</option>
                <option value="James">James</option>
              </select>
            </div>
            {labels.length > 0 && (
              <div className="label-filter">
                <label htmlFor="label-filter">Label:</label>
                <select
                  id="label-filter"
                  value={selectedLabel}
                  onChange={handleLabelFilterChange}
                  className="label-filter-dropdown"
                >
                  <option value="">All Labels</option>
                  {labels.map((label) => (
                    <option key={label} value={label}>
                      {formatLabelForDisplay(label)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {trainingSets.length > 0 && perPage > 1 && (
              <button className="layout-toggle-btn" onClick={toggleColumnLayout}>
                <span className="toggle-icon">{getLayoutIcon()}</span>
                {columnLayout} {columnLayout === 1 ? 'Column' : 'Columns'}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {trainingSets.length === 0 && !loading && !error && (
          <div className="empty-state">
            <div>No training sets found</div>
          </div>
        )}

        {trainingSets.length > 0 && (
          <>
            <PaginationControls
              pagination={pagination}
              perPage={perPage}
              onPerPageChange={handlePerPageChange}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              onPageChange={handlePageChange}
              position="top"
            />

            <div className={`training-sets-grid training-sets-grid-${columnLayout}-col`}>
              {trainingSets.map((trainingSet) => (
                <div key={trainingSet.id} className="training-set-card">
                  <div className="card-header">
                    <span className={`label-badge label-${trainingSet.label || 'unknown'}`}>
                      {trainingSet.label || 'N/A'}
                    </span>
                    <span className="card-id">#{trainingSet.id}</span>
                  </div>

                  <div className="card-body">
                    <div className="card-field">
                      <span className="field-label">Email:</span>
                      <span className="field-value">
                        {maskEmail(trainingSet.email)}
                      </span>
                    </div>

                    {trainingSet.ai_reasoning && (
                      <div className="card-section">
                        <h4 className="section-title">Reasoning</h4>
                        <p className="section-content">
                          {typeof trainingSet.ai_reasoning === 'string'
                            ? trainingSet.ai_reasoning
                            : JSON.stringify(trainingSet.ai_reasoning)}
                        </p>
                      </div>
                    )}

                    {trainingSet.ai_evidence && (
                      <div className="card-section">
                        <h4 className="section-title">Evidence</h4>
                        <ul className="evidence-list">
                          {parseEvidence(trainingSet.ai_evidence).map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(trainingSet.gary || trainingSet.james) && (
                      <div className="card-section label-comparison">
                        <h4 className="section-title">Label Comparison</h4>
                        <div className="label-comparison-content">
                          {trainingSet.gary && (
                            <div className="label-item">
                              <span className="label-source">Gary:</span>
                              <span className={`label-badge label-${trainingSet.gary || 'unknown'}`}>
                                {formatLabelForDisplay(trainingSet.gary)}
                              </span>
                            </div>
                          )}
                          {trainingSet.james && (
                            <div className="label-item">
                              <span className="label-source">James:</span>
                              <span className={`label-badge label-${trainingSet.james || 'unknown'}`}>
                                {formatLabelForDisplay(trainingSet.james)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="card-footer">
                      <span className="last-updated">
                        Last Updated: {formatDate(trainingSet.updated_at)}
                      </span>
                      <span className="updated-by">
                        By {trainingSet.updated_by || 'Ai'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <PaginationControls
              pagination={pagination}
              perPage={perPage}
              onPerPageChange={handlePerPageChange}
              onPreviousPage={handlePreviousPage}
              onNextPage={handleNextPage}
              onPageChange={handlePageChange}
              position="bottom"
            />
          </>
        )}
      </div>
    </div>
  )
}

export default TrainingSets
